import React, { forwardRef, useImperativeHandle, useState } from "react";

/**
 * Select dropdown component with various styling options
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Label text
 * @param {Array} props.options - Select options array
 * @param {boolean} props.required - Whether the select is required
 * @param {string} props.name - Select name
 * @param {string} props.placeholder - Placeholder text
 * @param {function} props.onChange - Change handler
 * @param {string} props.value - Controlled select value
 * @param {boolean} props.floating - Whether to use floating label design
 * @param {string} props.className - Additional CSS classes
 */
const Select = forwardRef((props, ref) => {
	const {
		label,
		options = [],
		required,
		name,
		placeholder = "Bitte auswählen...",
		onChange,
		value,
		className = "",
		floating = true,
		error,
		...rest
	} = props;

	const [isFocused, setIsFocused] = useState(false);
	const hasValue = value !== undefined && value !== null && value !== "";

	useImperativeHandle(ref, () => ({
		reset: () => {
			const selectElement = document.querySelector(`[name="${name}"]`);
			if (selectElement) {
				selectElement.value = "";
				if (onChange) {
					onChange({
						target: {
							name,
							value: ""
						}
					});
				}
			}
		}
	}));

	const handleFocus = () => setIsFocused(true);
	const handleBlur = () => setIsFocused(false);
	
	// Standard (non-floating) select with label
	if (!floating) {
		return (
			<div className={`${className} mb-3`}>
				{label && (
					<label 
						htmlFor={rest.id || name} 
						className="form-label">
						{label}
						{required && <span className="text-danger ms-1">*</span>}
					</label>
				)}
				<select 
				    id={rest.id || name}
					className={`form-select ${error ? 'is-invalid' : ''}`}
					name={name}
					data-field={name}
					required={required}
					onChange={onChange}
					{...(value !== undefined ? { value } : {})}
					onFocus={handleFocus}
					onBlur={handleBlur}
					defaultValue=""
					{...rest}>
					<option value="" disabled>{placeholder}</option>
					{options.map((option, index) => (
						<option 
							key={option.value || option.option || index} 
							value={option.value || option.option}>
							{option.label || option.value || option.option}
						</option>
					))}
				</select>
				{error && <div className="invalid-feedback">{error}</div>}
			</div>
		);
	}

	// Floating label select (original implementation)
	// defaultValue={value || ""}
	return (
		<div className={className}>
			<div className="form-floating mb-3">
				<select
					id={rest.id || name}
					className={`form-select ${error ? 'is-invalid' : ''}`}
					name={name}
					data-field={name}
					required={required}
					onChange={onChange}
					{...(value !== undefined ? { value } : {})}
					onFocus={handleFocus}
					onBlur={handleBlur}
					defaultValue=""
					{...rest}>
					<option value="" disabled>{placeholder}</option>
					{options.map((option, index) => (
						<option 
						key={option.value || option.option || index} 
						value={option.value || option.option}>
						{option.label || option.value || option.option}
						</option>
					))}
				</select>
				<label 
					htmlFor={rest.id || name}
					className={isFocused || hasValue ? 'focused' : ''}>
					{label}
				</label>
			</div>
			{error && <div className="invalid-feedback d-block">{error}</div>}
		</div>
	);
});

Select.displayName = 'Select';

export default Select;
